import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
  Audio,
  staticFile,
} from "remotion";
import {
  Activity,
  Cpu,
  ShieldCheck,
  Zap,
  Terminal,
  CheckCircle2,
  Sparkles,
  Layers,
  ArrowUpRight,
} from "lucide-react";

export const SocialMediaDetailedMotion: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Helper spring
  const sp = (delay: number, damping = 14, stiffness = 120, mass = 0.9) =>
    spring({
      frame: Math.max(0, frame - delay),
      fps,
      config: { damping, stiffness, mass },
    });

  // Camera & global stage motion (subtle punch-in at impact)
  const cameraZoom = interpolate(frame, [0, 40, 120, 180], [0.94, 1.0, 1.02, 1.04], {
    extrapolateRight: "clamp",
  });

  // Background spotlight pulse
  const spotPulse = Math.sin(frame / 20) * 0.15 + 0.85;

  // Header entrance
  const headerSpring = sp(5, 15, 140);
  const headerY = interpolate(headerSpring, [0, 1], [-40, 0]);
  const headerOpacity = interpolate(headerSpring, [0, 1], [0, 1]);

  // Card 1 (Back Deck Card) Entrance
  const backCardSpring = sp(15, 15, 110);
  const backCardY = interpolate(backCardSpring, [0, 1], [120, 0]);
  const backCardRotZ = interpolate(backCardSpring, [0, 1], [-18, -7]);
  const backCardScale = interpolate(backCardSpring, [0, 1], [0.8, 0.92]);

  // Card 2 (Hero Front Card) 3D Entrance
  const heroSpring = sp(25, 13, 115, 1.0);
  const heroY = interpolate(heroSpring, [0, 1], [220, 0]);
  const heroRotX = interpolate(heroSpring, [0, 1], [24, 8]);
  const heroRotY = interpolate(heroSpring, [0, 1], [-28, -10]);
  const heroRotZ = interpolate(heroSpring, [0, 1], [6, 2]);
  const heroScale = interpolate(heroSpring, [0, 1], [0.85, 1.0]);
  const heroOpacity = interpolate(heroSpring, [0, 0.5, 1], [0, 0.9, 1]);

  // Floating gentle bobbing once landed
  const floatBob = frame > 45 ? Math.sin((frame - 45) / 18) * 8 : 0;
  const floatBobRotX = frame > 45 ? Math.cos((frame - 45) / 22) * 1.5 : 0;

  // Specular reflection sweep across the glass card
  const sheenSweep = interpolate(frame, [38, 75], [-120, 220], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Satellite Badges Entrance
  const badge1Spring = sp(50, 12, 160);
  const badge2Spring = sp(65, 12, 160);
  const badge3Spring = sp(80, 12, 160);

  // Live Counter Ticker (Interpolates smoothly from 0.00 to 99.98)
  const counterVal = interpolate(frame, [35, 95], [0, 99.98], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Live Latency Ticker (starts at 180ms down to 12.4ms)
  const latencyVal = interpolate(frame, [30, 85], [184, 12.4], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Terminal typewriter text progressive revealing
  const terminalText1 = "$ hermes render --quality=masterpiece";
  const charCount1 = Math.floor(
    interpolate(frame, [45, 80], [0, terminalText1.length], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    })
  );

  const terminalText2 = "✔ 3D Depth Engine: 1200px perspective mounted";
  const charCount2 = Math.floor(
    interpolate(frame, [85, 115], [0, terminalText2.length], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    })
  );

  const terminalText3 = "✔ 60fps Micro-Physics: Staggered spring active";
  const charCount3 = Math.floor(
    interpolate(frame, [120, 150], [0, terminalText3.length], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    })
  );

  // Equalizer bar heights
  const bars = [0.4, 0.85, 0.65, 1.0, 0.5, 0.9, 0.7, 0.45, 0.95, 0.6];

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#07040D",
        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
        color: "#FFFFFF",
        overflow: "hidden",
      }}
    >
      {/* SFX & AUDIO INTEGRATION */}
      <Audio src={staticFile("bgm-clean.mp3")} volume={0.18} />
      {frame === 24 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.6} />}
      {frame === 50 && <Audio src={staticFile("click-soft.mp3")} volume={0.5} />}
      {frame === 65 && <Audio src={staticFile("pop.mp3")} volume={0.5} />}
      {frame === 80 && <Audio src={staticFile("ui-success.mp3")} volume={0.65} />}

      {/* AMBIENT RADIAL LIGHTING & GLOW */}
      <div
        style={{
          position: "absolute",
          top: "20%",
          left: "15%",
          width: 700,
          height: 700,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(139, 92, 246, 0.28) 0%, rgba(139, 92, 246, 0) 70%)",
          filter: "blur(90px)",
          transform: `scale(${spotPulse})`,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "45%",
          right: "10%",
          width: 600,
          height: 600,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(6, 182, 212, 0.22) 0%, rgba(6, 182, 212, 0) 70%)",
          filter: "blur(100px)",
          pointerEvents: "none",
        }}
      />

      {/* SUBTLE BACKGROUND GRID MESH */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(to right, rgba(255, 255, 255, 0.035) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 255, 255, 0.035) 1px, transparent 1px)
          `,
          backgroundSize: "60px 60px",
          maskImage: "radial-gradient(circle at 50% 50%, black 40%, transparent 85%)",
          WebkitMaskImage: "radial-gradient(circle at 50% 50%, black 40%, transparent 85%)",
        }}
      />

      {/* FLOATING MICRO PARTICLES */}
      {[...Array(12)].map((_, i) => {
        const pY = ((frame * (1 + (i % 3) * 0.5) + i * 160) % 1920);
        const pX = (i * 90 + Math.sin(frame / 20 + i) * 30) % 1080;
        const pOpacity = (Math.sin((frame + i * 20) / 15) + 1) * 0.25;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              top: 1920 - pY,
              left: pX,
              width: i % 2 === 0 ? 4 : 6,
              height: i % 2 === 0 ? 4 : 6,
              borderRadius: "50%",
              backgroundColor: i % 3 === 0 ? "#00F0FF" : "#A855F7",
              opacity: pOpacity,
              filter: "blur(1px)",
            }}
          />
        );
      })}

      {/* STAGE CONTAINER WITH CAMERA ZOOM */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          transform: `scale(${cameraZoom})`,
          transformOrigin: "center center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "0 60px",
        }}
      >
        {/* ============================================================ */}
        {/* SAFE ZONE HEADER (TOP: 230px) */}
        {/* ============================================================ */}
        <div
          style={{
            marginTop: 240,
            width: "100%",
            maxWidth: 960,
            opacity: headerOpacity,
            transform: `translateY(${headerY}px)`,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          {/* KICKER BADGE */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 18px",
              borderRadius: 999,
              background: "rgba(139, 92, 246, 0.15)",
              border: "1px solid rgba(168, 85, 247, 0.4)",
              backdropFilter: "blur(12px)",
              marginBottom: 18,
            }}
          >
            <Sparkles size={16} color="#00F0FF" />
            <span
              style={{
                fontSize: 16,
                fontWeight: 800,
                letterSpacing: 2.5,
                color: "#E2E8F0",
                textTransform: "uppercase",
              }}
            >
              Remotion High-Precision Motion
            </span>
          </div>

          {/* DUAL-TONE HEADLINE */}
          <h1
            style={{
              margin: 0,
              fontSize: 60,
              fontWeight: 900,
              lineHeight: 1.12,
              letterSpacing: -1.5,
            }}
          >
            BUKAN ANIMASI BIASA.
            <br />
            <span
              style={{
                background: "linear-gradient(90deg, #00F0FF 0%, #A855F7 60%, #EC4899 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              INI DETAIL KELAS MEDSOS.
            </span>
          </h1>

          <p
            style={{
              margin: "12px 0 0 0",
              fontSize: 22,
              color: "#94A3B8",
              fontWeight: 500,
              letterSpacing: -0.3,
            }}
          >
            3D perspective tilt, spring micro-physics, dynamic telemetry, & pure vector icons.
          </p>
        </div>

        {/* ============================================================ */}
        {/* MAIN 3D SHOWCASE STAGE */}
        {/* ============================================================ */}
        <div
          style={{
            position: "relative",
            width: "100%",
            maxWidth: 960,
            height: 820,
            marginTop: 40,
            perspective: 1400,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* ------------------------------------------------------------ */}
          {/* BACK CARD (THE UNDER-DECK / FAN CARD) */}
          {/* ------------------------------------------------------------ */}
          <div
            style={{
              position: "absolute",
              width: 820,
              height: 520,
              borderRadius: 32,
              background: "linear-gradient(145deg, rgba(22, 14, 38, 0.75), rgba(12, 8, 24, 0.85))",
              border: "1px solid rgba(139, 92, 246, 0.2)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              transform: `translateY(${backCardY}px) scale(${backCardScale}) rotateZ(${backCardRotZ}deg)`,
              boxShadow: "0 30px 60px rgba(0, 0, 0, 0.6)",
              padding: "28px 36px",
              opacity: interpolate(backCardSpring, [0, 1], [0, 0.7]),
              filter: "blur(1.5px)",
              pointerEvents: "none",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Layers size={20} color="#8B5CF6" />
                <span style={{ fontSize: 16, fontWeight: 700, color: "#A78BFA" }}>
                  BACKGROUND ENGINE STACK
                </span>
              </div>
              <span style={{ fontSize: 14, fontFamily: "monospace", color: "#64748B" }}>
                THREAD #572
              </span>
            </div>
          </div>

          {/* ------------------------------------------------------------ */}
          {/* HERO FRONT CARD (3D PERSPECTIVE TILT & SPECULAR SHEEN) */}
          {/* ------------------------------------------------------------ */}
          <div
            style={{
              position: "relative",
              width: 860,
              height: 580,
              borderRadius: 32,
              background: "linear-gradient(140deg, rgba(28, 18, 52, 0.85) 0%, rgba(13, 9, 26, 0.95) 100%)",
              border: "1.5px solid rgba(0, 240, 255, 0.35)",
              boxShadow: `
                0 40px 90px -15px rgba(0, 0, 0, 0.8),
                0 0 50px -10px rgba(139, 92, 246, 0.25),
                inset 0 1px 1px rgba(255, 255, 255, 0.2)
              `,
              backdropFilter: "blur(30px)",
              WebkitBackdropFilter: "blur(30px)",
              transform: `
                translateY(${heroY + floatBob}px)
                scale(${heroScale})
                rotateX(${heroRotX + floatBobRotX}deg)
                rotateY(${heroRotY}deg)
                rotateZ(${heroRotZ}deg)
              `,
              opacity: heroOpacity,
              overflow: "hidden",
              padding: "32px 36px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            {/* SPECULAR SHINE / LIGHT SWEEP */}
            <div
              style={{
                position: "absolute",
                top: -100,
                bottom: -100,
                left: 0,
                width: 140,
                background:
                  "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.22), rgba(0, 240, 255, 0.35), transparent)",
                transform: `translateX(${sheenSweep}%) skewX(-25deg)`,
                pointerEvents: "none",
              }}
            />

            {/* CARD TOP BAR (MAC CONTROLS + TELEMETRY PILL) */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingBottom: 20,
                borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
              }}
            >
              {/* MAC DOTS */}
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#FF5F56" }} />
                <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#FFBD2E" }} />
                <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#27C93F" }} />
                <span
                  style={{
                    marginLeft: 12,
                    fontSize: 15,
                    fontFamily: "monospace",
                    color: "#94A3B8",
                    fontWeight: 600,
                  }}
                >
                  live-telemetry.runtime
                </span>
              </div>

              {/* STATUS INDICATOR PILL */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 14px",
                  borderRadius: 999,
                  background: "rgba(16, 185, 129, 0.15)",
                  border: "1px solid rgba(16, 185, 129, 0.35)",
                }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "#10B981",
                    boxShadow: "0 0 10px #10B981",
                  }}
                />
                <span style={{ fontSize: 13, fontWeight: 700, color: "#10B981", letterSpacing: 0.5 }}>
                  SYS.OPERATIONAL
                </span>
              </div>
            </div>

            {/* CARD MIDDLE BODY (METRICS DUAL STAGE) */}
            <div style={{ display: "flex", gap: 24, margin: "20px 0" }}>
              {/* METRIC 1: COUNTER TICKER */}
              <div
                style={{
                  flex: 1.1,
                  padding: "20px 24px",
                  borderRadius: 20,
                  background: "rgba(10, 6, 18, 0.6)",
                  border: "1px solid rgba(255, 255, 255, 0.06)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: "#94A3B8" }}>
                    FRAME RENDERING ACCURACY
                  </span>
                  <Activity size={18} color="#00F0FF" />
                </div>
                <div
                  style={{
                    fontSize: 54,
                    fontWeight: 900,
                    letterSpacing: -1,
                    fontFamily: "monospace",
                    color: "#FFFFFF",
                    margin: "8px 0 4px 0",
                  }}
                >
                  {counterVal.toFixed(2)}%
                </div>
                {/* DYNAMIC PROGRESS BAR */}
                <div
                  style={{
                    width: "100%",
                    height: 6,
                    borderRadius: 999,
                    background: "rgba(255, 255, 255, 0.08)",
                    overflow: "hidden",
                    marginTop: 10,
                  }}
                >
                  <div
                    style={{
                      width: `${counterVal}%`,
                      height: "100%",
                      background: "linear-gradient(90deg, #00F0FF, #8B5CF6)",
                      boxShadow: "0 0 12px #00F0FF",
                    }}
                  />
                </div>
              </div>

              {/* METRIC 2: LATENCY & WAVEFORM */}
              <div
                style={{
                  flex: 0.9,
                  padding: "20px 24px",
                  borderRadius: 20,
                  background: "rgba(10, 6, 18, 0.6)",
                  border: "1px solid rgba(255, 255, 255, 0.06)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: "#94A3B8" }}>
                    EXECUTION LATENCY
                  </span>
                  <Zap size={18} color="#FFE500" />
                </div>

                <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                  <span
                    style={{
                      fontSize: 42,
                      fontWeight: 900,
                      fontFamily: "monospace",
                      color: "#FFE500",
                    }}
                  >
                    {latencyVal.toFixed(1)}
                  </span>
                  <span style={{ fontSize: 18, color: "#94A3B8", fontWeight: 700 }}>ms</span>
                </div>

                {/* ANIMATED WAVEFORM BARS */}
                <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 26 }}>
                  {bars.map((h, idx) => {
                    const waveH = Math.min(
                      26,
                      Math.max(6, h * 26 + Math.sin(frame / 6 + idx * 1.2) * 8)
                    );
                    return (
                      <div
                        key={idx}
                        style={{
                          flex: 1,
                          height: waveH,
                          borderRadius: 3,
                          background: idx % 2 === 0 ? "#8B5CF6" : "#00F0FF",
                          opacity: 0.85,
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            </div>

            {/* CARD BOTTOM: EMBEDDED MICRO TERMINAL */}
            <div
              style={{
                borderRadius: 18,
                background: "rgba(5, 3, 10, 0.8)",
                border: "1px solid rgba(0, 240, 255, 0.2)",
                padding: "16px 20px",
                fontFamily: "monospace",
                fontSize: 15,
                color: "#E2E8F0",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#64748B", fontSize: 12 }}>
                <Terminal size={14} color="#00F0FF" />
                <span>SHELL EXECUTION LOG // STAGGERED THREAD</span>
              </div>
              <div style={{ color: "#00F0FF", fontWeight: 700 }}>
                {terminalText1.slice(0, charCount1)}
                {frame >= 45 && frame <= 85 && (
                  <span style={{ opacity: frame % 16 < 8 ? 1 : 0 }}>▍</span>
                )}
              </div>
              {charCount2 > 0 && (
                <div style={{ color: "#10B981" }}>
                  {terminalText2.slice(0, charCount2)}
                </div>
              )}
              {charCount3 > 0 && (
                <div style={{ color: "#C084FC" }}>
                  {terminalText3.slice(0, charCount3)}
                  {frame > 120 && (
                    <span style={{ opacity: frame % 16 < 8 ? 1 : 0 }}>▍</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ============================================================ */}
          {/* FLOATING SATELLITE BADGES (HIGH DEPTH MICRO-ELEMENTS) */}
          {/* ============================================================ */}
          {/* SATELLITE 1: TOP RIGHT (ZERO SLOP IN LIME) */}
          <div
            style={{
              position: "absolute",
              top: 40,
              right: 10,
              padding: "14px 22px",
              borderRadius: 20,
              background: "rgba(15, 23, 42, 0.85)",
              border: "1.5px solid #D8FF3E",
              boxShadow: "0 20px 40px rgba(0,0,0,0.5), 0 0 25px rgba(216, 255, 62, 0.3)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              display: "flex",
              alignItems: "center",
              gap: 10,
              transform: `
                translateY(${interpolate(badge1Spring, [0, 1], [60, 0])}px)
                scale(${interpolate(badge1Spring, [0, 1], [0.5, 1])})
                rotate(4deg)
              `,
              opacity: interpolate(badge1Spring, [0, 1], [0, 1]),
            }}
          >
            <ShieldCheck size={24} color="#D8FF3E" />
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#94A3B8" }}>STANDARD</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: "#D8FF3E" }}>ZERO SLOP</div>
            </div>
          </div>

          {/* SATELLITE 2: BOTTOM LEFT (60 FPS PHYSICS) */}
          <div
            style={{
              position: "absolute",
              bottom: 30,
              left: 10,
              padding: "14px 22px",
              borderRadius: 20,
              background: "rgba(15, 23, 42, 0.85)",
              border: "1.5px solid #00F0FF",
              boxShadow: "0 20px 40px rgba(0,0,0,0.5), 0 0 25px rgba(0, 240, 255, 0.3)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              display: "flex",
              alignItems: "center",
              gap: 12,
              transform: `
                translateY(${interpolate(badge2Spring, [0, 1], [-60, 0])}px)
                scale(${interpolate(badge2Spring, [0, 1], [0.5, 1])})
                rotate(-5deg)
              `,
              opacity: interpolate(badge2Spring, [0, 1], [0, 1]),
            }}
          >
            <Cpu size={24} color="#00F0FF" />
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#94A3B8" }}>PHYSICS ENGINE</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: "#00F0FF" }}>60 FPS SPRING</div>
            </div>
          </div>

          {/* SATELLITE 3: BOTTOM RIGHT ACTION PILL */}
          <div
            style={{
              position: "absolute",
              bottom: 70,
              right: 25,
              padding: "12px 20px",
              borderRadius: 999,
              background: "linear-gradient(90deg, #8B5CF6, #EC4899)",
              boxShadow: "0 15px 35px rgba(236, 72, 153, 0.4)",
              display: "flex",
              alignItems: "center",
              gap: 8,
              transform: `
                scale(${interpolate(badge3Spring, [0, 1], [0.4, 1])})
                rotate(2deg)
              `,
              opacity: interpolate(badge3Spring, [0, 1], [0, 1]),
            }}
          >
            <CheckCircle2 size={18} color="#FFFFFF" />
            <span style={{ fontSize: 15, fontWeight: 800, color: "#FFFFFF" }}>
              STUDIO VERIFIED
            </span>
          </div>
        </div>

        {/* ============================================================ */}
        {/* SAFE ZONE BOTTOM FOOTER (ABOVE Y: 1520px) */}
        {/* ============================================================ */}
        <div
          style={{
            marginTop: 40,
            width: "100%",
            maxWidth: 960,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px 28px",
            borderRadius: 24,
            background: "rgba(255, 255, 255, 0.04)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            backdropFilter: "blur(16px)",
            opacity: interpolate(heroSpring, [0, 1], [0, 1]),
            transform: `translateY(${interpolate(heroSpring, [0, 1], [40, 0])}px)`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 14,
                background: "linear-gradient(135deg, #00F0FF, #8B5CF6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Zap size={24} color="#07040D" />
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#FFFFFF" }}>
                Zura Motion Production Pipeline
              </div>
              <div style={{ fontSize: 14, color: "#94A3B8" }}>
                Thread #572 • @zura.ailab & Kyu Ecosystem
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 20px",
              borderRadius: 999,
              background: "#FFFFFF",
              color: "#07040D",
              fontWeight: 800,
              fontSize: 15,
            }}
          >
            <span>Buka Studio</span>
            <ArrowUpRight size={18} color="#07040D" />
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};
